import { parseXml, XmlElement } from "@rgrove/parse-xml";
import { alphaBounds, opaqueCorners } from "../plates/rgba.js";
import { illustrationIssues } from "./illustration.js";
import { StoryletLibrarySchema, AssetPackManifestSchema, EnvironmentDefinitionSchema, PrologueSceneSchema, LINE_WEIGHTS, OCCLUSION_PAINT, OCCLUSION_CASING, PLATE_MAX_BYTES, PLATE_MAX_PX, } from "@pushback-and-chaos/contracts";
const ALLOWED_WEIGHTS = new Set(Object.values(LINE_WEIGHTS).map((w) => String(w)));
/** currentColor everywhere, with exactly one accent exception (ADR 0009). */
const ALLOWED_STROKES = new Set(["currentColor", "var(--pc-string, #B06A6A)"]);
const FORBIDDEN_ROOT_ATTRS = ["class", "transform", "style"];
function elementsOf(node) {
    return node instanceof XmlElement ? [node, ...node.children.flatMap(elementsOf)] : [];
}
export function validateAssets(input) {
    const issues = [];
    const coverage = [];
    const manifests = [];
    const unreferenced = [];
    const allSymbolIds = new Map(); // id -> pack dir
    for (const pack of input.packs) {
        let manifest;
        try {
            manifest = AssetPackManifestSchema.parse(JSON.parse(pack.manifestText));
        }
        catch (error) {
            issues.push({ where: `${pack.dir}/pack.json`, message: `manifest does not parse: ${error instanceof Error ? error.message.slice(0, 200) : String(error)}` });
            continue;
        }
        manifests.push(manifest);
        const declared = new Set(manifest.symbols.flatMap((symbol) => [symbol.file, ...(symbol.plate === undefined ? [] : [symbol.plate.file])]));
        for (const file of [...pack.files.keys(), ...(pack.binaries?.keys() ?? [])])
            if (!declared.has(file))
                unreferenced.push(`${pack.dir}/${file}`);
        const profileIds = manifest.visualProfiles?.map((profile) => profile.id) ?? [];
        if (new Set(profileIds).size !== profileIds.length)
            issues.push({ where: `${pack.dir}/pack.json`, message: "visual profile IDs must be unique within a pack" });
        let licensed = 0;
        for (const symbol of manifest.symbols) {
            const where = `${pack.dir}/${symbol.file}`;
            // ---- id uniqueness across ALL installed packs ----
            const existing = allSymbolIds.get(symbol.id);
            if (existing !== undefined) {
                issues.push({ where, message: `symbol id ${symbol.id} already declared by ${existing}` });
            }
            allSymbolIds.set(symbol.id, pack.dir);
            // ---- namespace ----
            if (!symbol.id.startsWith(`${manifest.packId}.`)) {
                issues.push({ where, message: `symbol id ${symbol.id} is not namespaced under "${manifest.packId}."` });
            }
            // ---- file exists, parses, holds exactly one matching <symbol> ----
            const text = pack.files.get(symbol.file);
            if (text === undefined) {
                issues.push({ where, message: "declared file does not exist" });
                continue;
            }
            let root;
            try {
                root = parseXml(text).root;
            }
            catch (error) {
                issues.push({ where, message: `file does not parse as XML: ${error instanceof Error ? error.message.slice(0, 120) : String(error)}` });
                continue;
            }
            if (root === null) {
                issues.push({ where, message: "file has no root element" });
                continue;
            }
            const symbolEls = elementsOf(root).filter((e) => e.name === "symbol");
            if (symbolEls.length !== 1 || root.name !== "symbol") {
                issues.push({ where, message: `file must hold exactly one root <symbol> (found ${String(symbolEls.length)}, root <${root.name}>)` });
                continue;
            }
            const symbolEl = symbolEls[0];
            if (symbolEl === undefined)
                continue;
            if (symbolEl.attributes["id"] !== symbol.id) {
                issues.push({ where, message: `<symbol id="${symbolEl.attributes["id"] ?? ""}"> does not match manifest id ${symbol.id}` });
            }
            // ---- viewBox equals footprint ----
            const viewBox = (symbolEl.attributes["viewBox"] ?? "").split(/\s+/).map(Number);
            const [vx, vy, vw, vh] = [viewBox[0] ?? Number.NaN, viewBox[1] ?? Number.NaN, viewBox[2] ?? Number.NaN, viewBox[3] ?? Number.NaN];
            if (vx !== 0 || vy !== 0 || Math.abs(vw - symbol.footprint.w) > 0.02 || Math.abs(vh - symbol.footprint.h) > 0.02) {
                issues.push({ where, message: `viewBox "${symbolEl.attributes["viewBox"] ?? ""}" does not equal footprint 0 0 ${String(symbol.footprint.w)} ${String(symbol.footprint.h)}` });
            }
            // ---- forbidden constructs ----
            for (const attr of FORBIDDEN_ROOT_ATTRS) {
                if (symbolEl.attributes[attr] !== undefined) {
                    issues.push({ where, message: `<symbol> root carries forbidden attribute "${attr}"` });
                }
            }
            const descendants = elementsOf(symbolEl);
            const plate = symbol.plate;
            for (const message of illustrationIssues(symbol, profileIds, text))
                issues.push({ where, message });
            const images = descendants.filter((e) => e.name === "image");
            for (const element of descendants) {
                if (element.name === "style")
                    issues.push({ where, message: "<style> inside a symbol cannot cross the <use> shadow boundary — presentation attributes only" });
                if (element.name === "image" && plate === undefined)
                    issues.push({ where, message: "<image> is forbidden in a symbol that declares no plate — vector only (ADR 0015)" });
                if (element !== symbolEl && element.attributes["class"] !== undefined) {
                    issues.push({ where, message: `<${element.name} class=...> — document CSS cannot cross the <use> boundary; use presentation attributes` });
                }
            }
            if (text.includes("base64"))
                issues.push({ where, message: "base64 payload found — a plate is referenced by file and inlined at build, never committed inline" });
            // ---- plate-backed symbols (ADR 0015): one <image> of the declared
            // plate, at the footprint; the bytes exist, hash, decode, carry alpha,
            // are tight-cropped, keep a transparent border, and match the scale ----
            if (plate !== undefined) {
                const image = images[0];
                if (images.length !== 1 || image === undefined) {
                    issues.push({ where, message: `a plate-backed symbol holds exactly one <image> (found ${String(images.length)})` });
                }
                else {
                    if (image.attributes["href"] !== plate.file)
                        issues.push({ where, message: `<image href="${image.attributes["href"] ?? ""}"> must reference the declared plate ${plate.file}` });
                    const num = (name) => Number(image.attributes[name] ?? Number.NaN);
                    if (plate.registration === undefined && (num("x") !== 0 || num("y") !== 0 || Math.abs(num("width") - symbol.footprint.w) > 0.02 || Math.abs(num("height") - symbol.footprint.h) > 0.02)) {
                        issues.push({ where, message: "<image> must sit at 0 0 with width/height equal to the footprint" });
                    }
                }
                const bytes = pack.binaries?.get(plate.file);
                const codec = input.plateCodec;
                if (bytes === undefined) {
                    issues.push({ where, message: `declared plate ${plate.file} does not exist` });
                }
                else if (codec === undefined) {
                    issues.push({ where, message: "plate-backed symbol validated without a plate codec — run through assets:validate" });
                }
                else {
                    const byteCap = plate.registration === undefined ? PLATE_MAX_BYTES : 6_000_000;
                    if (bytes.length > byteCap)
                        issues.push({ where, message: `plate is ${String(bytes.length)} bytes; the cap is ${String(byteCap)}` });
                    const digest = codec.sha256(bytes);
                    if (digest !== plate.sha256)
                        issues.push({ where, message: `plate bytes hash to ${digest.slice(0, 12)}…, manifest pins ${plate.sha256.slice(0, 12)}… — re-ingest or restore the reviewed plate` });
                    const decoded = codec.decode(bytes);
                    if (!decoded.ok)
                        issues.push({ where, message: `plate does not decode: ${decoded.error}` });
                    else {
                        const png = decoded.value;
                        if (!png.hasAlpha && plate.registration === undefined)
                            issues.push({ where, message: "plate has no alpha channel — a plate's transparency is its occlusion" });
                        if (png.width !== plate.width || png.height !== plate.height)
                            issues.push({ where, message: `plate is ${String(png.width)}x${String(png.height)} px, manifest says ${String(plate.width)}x${String(plate.height)}` });
                        if (png.width > PLATE_MAX_PX || png.height > PLATE_MAX_PX)
                            issues.push({ where, message: `plate exceeds ${String(PLATE_MAX_PX)} px on a side` });
                        if (plate.registration === undefined && (Math.abs(png.width - symbol.footprint.w * plate.pxPerCm) > 1.5 || Math.abs(png.height - symbol.footprint.h * plate.pxPerCm) > 1.5)) {
                            issues.push({ where, message: `plate pixels do not equal footprint × pxPerCm (${String(symbol.footprint.w)}×${String(plate.pxPerCm)} by ${String(symbol.footprint.h)}×${String(plate.pxPerCm)})` });
                        }
                        if (plate.registration === undefined) {
                            const bounds = alphaBounds(png);
                            if (bounds === null)
                                issues.push({ where, message: "plate is fully transparent" });
                            else if (plate.fitted === true) {
                                if (bounds.y1 !== png.height)
                                    issues.push({ where, message: "fitted plate's subject must touch the bottom edge (feet on the baseline)" });
                            }
                            else if (bounds.x0 !== 0 || bounds.y0 !== 0 || bounds.x1 !== png.width || bounds.y1 !== png.height) {
                                issues.push({ where, message: "plate is not tight-cropped to its alpha bounds (viewBox must be the subject's own box; feet on the bottom edge)" });
                            }
                            if (png.hasAlpha && opaqueCorners(png) === 4)
                                issues.push({ where, message: "plate has a baked background — all four corners are opaque (the section's paper is the paper)" });
                        }
                    }
                }
            }
            // ---- strokes and weights ----
            for (const element of descendants) {
                if (element.attributes["data-part"] === "occlude") {
                    // The occlusion layer (contracts OCCLUSION_PAINT): paper-colored
                    // silhouette + casing. Its paint is pinned — any other color
                    // would punch a visible hole in the drawing.
                    if (element.attributes["fill"] !== OCCLUSION_PAINT || element.attributes["stroke"] !== OCCLUSION_PAINT) {
                        issues.push({ where, message: `occlude layer must fill and stroke ${OCCLUSION_PAINT}` });
                    }
                    if (element.attributes["stroke-width"] !== String(OCCLUSION_CASING)) {
                        issues.push({ where, message: `occlude layer casing must be stroke-width ${String(OCCLUSION_CASING)}` });
                    }
                    continue;
                }
                const stroke = element.attributes["stroke"];
                if (stroke !== undefined && stroke !== "none" && !ALLOWED_STROKES.has(stroke)) {
                    issues.push({ where, message: `stroke "${stroke}" — must be currentColor or the single accent token` });
                }
                const width = element.attributes["stroke-width"];
                if (width !== undefined && !ALLOWED_WEIGHTS.has(width) && !ALLOWED_WEIGHTS.has(String(Number(width)))) {
                    issues.push({ where, message: `stroke-width "${width}" is not one of the four tokens (${[...ALLOWED_WEIGHTS].join(" / ")})` });
                }
            }
            // ---- declared parts exist as data-part ----
            const partsInFile = new Set(descendants.map((e) => e.attributes["data-part"]).filter((p) => p !== undefined));
            for (const part of symbol.parts) {
                if (!partsInFile.has(part))
                    issues.push({ where, message: `declared part "${part}" has no data-part group in the file` });
            }
            // figures must be articulated (animation requirement)
            const local = symbol.id.slice(manifest.packId.length + 1);
            if ((local.startsWith("fig.") || local.startsWith("cast.")) && symbol.parts.length === 0) {
                issues.push({ where, message: "figure symbols must declare at least one animation part (data-part)" });
            }
            // ---- slots inside footprint ----
            for (const slot of symbol.slots) {
                if (slot.x < 0 || slot.x > symbol.footprint.w || slot.y < 0 || slot.y > symbol.footprint.h) {
                    issues.push({ where, message: `slot "${slot.id}" (${String(slot.x)},${String(slot.y)}) lies outside the footprint` });
                }
            }
            // ---- licence entry ----
            if (pack.licensesText === null) {
                issues.push({ where, message: "pack has no LICENSES.md" });
            }
            else if (!pack.licensesText.includes(symbol.license)) {
                issues.push({ where, message: `licence key "${symbol.license}" has no entry in LICENSES.md — an unattributable symbol does not ship` });
            }
            else {
                licensed += 1;
            }
        }
        coverage.push({ pack: pack.dir, symbols: manifest.symbols.length, licensed, unlicensed: manifest.symbols.length - licensed });
    }
    // ---- every symbolRef in every environment resolves ----
    const checkDefinitionRefs = (where, definition) => {
        const refs = new Set();
        for (const fx of definition.fixtures)
            refs.add(fx.symbolRef);
        for (const prop of definition.props)
            refs.add(prop.symbolRef);
        for (const extra of definition.extras) {
            for (const p of extra.poses)
                refs.add(p);
            for (const c of extra.companions)
                refs.add(c);
        }
        for (const cast of definition.cast)
            refs.add(cast.symbolRef);
        // ---- a site visit's staging: every staged person is a known symbol at a
        // real station, under an id used once. A missing slot would otherwise
        // surface as a person drawn nowhere, in play. ----
        const staged = new Set();
        for (const moment of definition.visit?.moments ?? [])
            for (const actor of moment.actors) {
                const at = `${where}#visit.${moment.id}`;
                for (const ref of [actor.symbolRef, ...actor.propRefs])
                    refs.add(ref);
                const room = definition.rooms.find((r) => r.id === actor.room);
                if (room === undefined)
                    issues.push({ where: at, message: `stages ${actor.instanceId} in room "${actor.room}", which does not exist` });
                else if (!room.slots.some((slot) => slot.id === actor.slot && (slot.kind === "stand" || slot.kind === "seat" || slot.kind === "floor")))
                    issues.push({ where: at, message: `stages ${actor.instanceId} at "${actor.room}/${actor.slot}", which is not a place a person can be` });
                if (staged.has(actor.instanceId))
                    issues.push({ where: at, message: `stages ${actor.instanceId} twice; one person stands in one place` });
                staged.add(actor.instanceId);
            }
        for (const ref of refs) {
            // core.fx.clock / core.fx.calendar are Tier-A generated fixtures drawn
            // by the composer itself, not pack symbols.
            if (ref === "core.fx.clock" || ref === "core.fx.calendar")
                continue;
            if (!allSymbolIds.has(ref)) {
                issues.push({ where, message: `symbolRef ${ref} does not resolve in any installed pack` });
            }
        }
    };
    // ---- navigation (ADR 0014): every portal end, entry and exit is a
    // WALKABLE slot that exists; a dangling portal is a route that silently
    // never exists, so it is an error here rather than a surprise in play ----
    const WALKABLE = new Set(["stand", "seat", "floor"]);
    const checkNavigation = (where, definition) => {
        if (definition.navigation === null)
            return;
        const station = (label, ref) => {
            const room = definition.rooms.find((r) => r.id === ref.room);
            const slot = room?.slots.find((s) => s.id === ref.slot);
            if (room === undefined || slot === undefined)
                issues.push({ where, message: `navigation ${label} names ${ref.room}/${ref.slot}, which does not exist` });
            else if (!WALKABLE.has(slot.kind))
                issues.push({ where, message: `navigation ${label} names ${ref.room}/${ref.slot}, a ${slot.kind} slot — only stand/seat/floor slots are walkable` });
        };
        for (const portal of definition.navigation.portals) {
            station(`portal ${portal.id}.from`, portal.from);
            station(`portal ${portal.id}.to`, portal.to);
            if (portal.from.room === portal.to.room)
                issues.push({ where, message: `portal ${portal.id} joins ${portal.from.room} to itself — slots in one room are already mutually reachable` });
        }
        definition.navigation.entries.forEach((e, i) => station(`entries[${String(i)}]`, e));
        definition.navigation.exits.forEach((e, i) => station(`exits[${String(i)}]`, e));
    };
    const definitionsById = new Map();
    for (const [envFile, text] of input.environments) {
        let definition;
        try {
            definition = EnvironmentDefinitionSchema.parse(JSON.parse(text));
        }
        catch (error) {
            issues.push({ where: envFile, message: `environment does not parse: ${error instanceof Error ? error.message.slice(0, 200) : String(error)}` });
            continue;
        }
        definitionsById.set(definition.envId, definition);
        checkDefinitionRefs(envFile, definition);
        checkNavigation(envFile, definition);
        // ---- painted backplates (ADR 0015): bytes exist, hash, decode, pixels match; one per treatment ----
        const treatments = new Set();
        for (const bp of definition.backplates ?? []) {
            const where = `${envFile} backplate ${bp.treatment}`;
            if (treatments.has(bp.treatment))
                issues.push({ where, message: "duplicate backplate treatment" });
            treatments.add(bp.treatment);
            const bytes = input.backplates?.get(bp.file);
            const codec = input.plateCodec;
            if (bytes === undefined) {
                issues.push({ where, message: `declared backplate ${bp.file} does not exist` });
                continue;
            }
            if (codec === undefined) {
                issues.push({ where, message: "backplate validated without a plate codec — run through assets:validate" });
                continue;
            }
            const digest = codec.sha256(bytes);
            if (digest !== bp.sha256)
                issues.push({ where, message: `backplate bytes hash to ${digest.slice(0, 12)}…, definition pins ${bp.sha256.slice(0, 12)}…` });
            const decoded = codec.decode(bytes);
            if (!decoded.ok)
                issues.push({ where, message: `backplate does not decode: ${decoded.error}` });
            else if (decoded.value.width !== bp.width || decoded.value.height !== bp.height)
                issues.push({ where, message: `backplate is ${String(decoded.value.width)}x${String(decoded.value.height)} px, definition says ${String(bp.width)}x${String(bp.height)}` });
            // A painter's edit may drift by a percent or two per axis; the frame absorbs that as an invisible stretch. Beyond 4% it is a different picture.
            if (Math.abs(bp.frame.w / bp.frame.h / (bp.width / bp.height) - 1) > 0.04)
                issues.push({ where, message: "backplate frame (cm) and pixels differ in aspect by more than 4%; the image would visibly stretch" });
        }
    }
    // ---- storylets (ADR 0014): parse, and every actor/room/slot/fixture/prop
    // a directive or condition names exists in its environment. A storylet
    // cites an authored source by schema; what it stages must be stageable. ----
    for (const [file, text] of input.storylets ?? new Map()) {
        let library;
        try {
            library = StoryletLibrarySchema.parse(JSON.parse(text));
        }
        catch (error) {
            issues.push({ where: file, message: `storylet library does not parse: ${error instanceof Error ? error.message.slice(0, 200) : String(error)}` });
            continue;
        }
        const definition = definitionsById.get(library.envId);
        if (definition === undefined) {
            issues.push({ where: file, message: `storylets for ${library.envId}, but no such environment is authored` });
            continue;
        }
        const actorIds = new Set(["player", ...definition.cast.map((c) => c.symbolRef.split(".").pop() ?? "")]);
        const fixtureIds = new Set(definition.fixtures.map((f) => f.id));
        const checkActor = (where, id) => {
            if (id !== null && !actorIds.has(id))
                issues.push({ where, message: `names actor "${id}", not in this environment's cast (${[...actorIds].join(", ")})` });
        };
        const checkPlace = (where, place) => {
            if (place === null)
                return;
            const room = definition.rooms.find((r) => r.id === place.room);
            if (room === undefined)
                issues.push({ where, message: `names room "${place.room}", which does not exist` });
            else if (place.slot !== null && !room.slots.some((s) => s.id === place.slot))
                issues.push({ where, message: `names slot "${place.room}/${place.slot}", which does not exist` });
        };
        const seen = new Set();
        for (const storylet of library.storylets) {
            const where = `${file}#${storylet.id}`;
            if (seen.has(storylet.id))
                issues.push({ where, message: "duplicate storylet id" });
            seen.add(storylet.id);
            for (const condition of storylet.when) {
                if ("actor" in condition)
                    checkActor(where, condition.actor);
                if ("room" in condition)
                    checkPlace(where, { room: condition.room, slot: null });
                if ("fixture" in condition && !fixtureIds.has(condition.fixture))
                    issues.push({ where, message: `names fixture "${condition.fixture}", which does not exist` });
            }
            for (const directive of storylet.scene) {
                checkActor(where, directive.actor);
                checkActor(where, directive.counterpart);
                checkPlace(where, directive.place);
                if (directive.fixture !== null && !fixtureIds.has(directive.fixture))
                    issues.push({ where, message: `names fixture "${directive.fixture}", which does not exist` });
                if (directive.prop !== null && !allSymbolIds.has(directive.prop))
                    issues.push({ where, message: `names prop ${directive.prop}, which does not resolve in any installed pack` });
            }
        }
    }
    // ---- prologue heroes: schema + refs in both the definition and the fixed view ----
    for (const [heroFile, text] of input.prologues ?? new Map()) {
        let scene;
        try {
            scene = PrologueSceneSchema.parse(JSON.parse(text));
        }
        catch (error) {
            issues.push({ where: heroFile, message: `prologue scene does not parse: ${error instanceof Error ? error.message.slice(0, 200) : String(error)}` });
            continue;
        }
        checkDefinitionRefs(heroFile, scene.definition);
        for (const actor of scene.view.actors) {
            if (!allSymbolIds.has(actor.symbolRef)) {
                issues.push({ where: heroFile, message: `view actor symbolRef ${actor.symbolRef} does not resolve in any installed pack` });
            }
        }
        for (const prop of scene.view.props) {
            if (!allSymbolIds.has(prop.symbolRef)) {
                issues.push({ where: heroFile, message: `view prop symbolRef ${prop.symbolRef} does not resolve in any installed pack` });
            }
        }
    }
    return { issues, coverage, manifests, unreferenced: unreferenced.sort() };
}
export function coverageTable(coverage) {
    const lines = ["pack                      symbols  licensed  unlicensed"];
    for (const row of coverage) {
        lines.push(`${row.pack.padEnd(26)}${String(row.symbols).padStart(7)}${String(row.licensed).padStart(10)}${String(row.unlicensed).padStart(12)}`);
    }
    return lines.join("\n");
}
//# sourceMappingURL=validate.js.map